import type { UUID } from '@cmg/common';
import { ServerDataGrid, useDataGridContext } from '@cmg/data-grid';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useRefetchOnReactiveVar } from '../../../../../../../common/hooks/useRefetchOnReactiveVar/useRefetchOnReactiveVar';
import type { RolodexRecordStatus } from '../../../../../../../graphql';
import { useUpdateRelationship } from '../../../../components/shared-columns/hooks/useUpdateRelationship';
import { investorCoverageColumns } from './columns/InvestorCoverageColumns';
import { useCellValueChanged } from './hooks/useCellValueChanged';
import { useInvestorCoverageGridDataSource } from './hooks/useInvestorCoverageGridDataSource';
import type {
  InvestorCoverageGridContext,
  InvestorCoverageRow,
} from './InvestorCoverageGrid.model';
import {
  AUTO_SIZE_STRATEGY,
  canEmployeeEditCoverage,
  INITIAL_INVESTOR_COVERAGE_GRID_STATE,
  refetchFirmInvestorCoverageGridReactiveVar,
} from './InvestorCoverageGrid.model';

export type InvestorCoverageGridProps = Readonly<{
  employeeId: string;
  employeeStatus: RolodexRecordStatus;
}>;

export function InvestorCoverageGrid({ employeeId, employeeStatus }: InvestorCoverageGridProps) {
  const { crmIntegrationId } = useParams<{
    crmIntegrationId?: UUID;
  }>();
  const dataSource = useInvestorCoverageGridDataSource({
    tenantId: crmIntegrationId,
    employeeId,
  });

  const { refreshServerSide, redrawRows } = useDataGridContext();
  const handleUpdateRelationship = useUpdateRelationship({
    tenantId: crmIntegrationId,
    onComplete: refreshServerSide,
  });

  const handleCellValueChanged = useCellValueChanged({
    onUpdateRelationship: handleUpdateRelationship,
  });

  const context = useMemo(() => {
    return {
      isEditable: canEmployeeEditCoverage(employeeStatus),
    };
  }, [employeeStatus]);

  useEffect(
    function switchEditableCells() {
      redrawRows();
    },
    [context.isEditable, redrawRows]
  );

  useRefetchOnReactiveVar({
    onRefetch: refreshServerSide,
    reactiveVar: refetchFirmInvestorCoverageGridReactiveVar,
  });

  return (
    <ServerDataGrid<InvestorCoverageRow, InvestorCoverageGridContext>
      serverSideDatasource={dataSource}
      columnDefs={investorCoverageColumns}
      autoSizeStrategy={AUTO_SIZE_STRATEGY}
      context={context}
      onCellValueChanged={handleCellValueChanged}
      initialState={INITIAL_INVESTOR_COVERAGE_GRID_STATE}
    />
  );
}
